import api from '@/shared/utils/apiConfig';
import { parseFilenameFromContentDisposition } from '@/modules/print/utils/pdfBlob';
import type { PdfDisposition, PdfResponse } from '@/modules/print/types/print.types';

interface AxiosBlobError {
  response?: {
    data?: Blob;
    status?: number;
  };
  message?: string;
}

export const parseBlobError = async (error: unknown): Promise<string> => {
  const axiosError = error as AxiosBlobError;
  const data = axiosError.response?.data;

  if (data instanceof Blob) {
    try {
      const text = await data.text();
      const parsed = JSON.parse(text) as { message?: string };
      if (parsed.message) return parsed.message;
    } catch {
      // fall through to default message
    }
  }

  if (axiosError.message) return axiosError.message;
  return 'Failed to generate PDF';
};

export const fetchPdfBlob = async (
  path: string,
  disposition: PdfDisposition = 'inline',
  fallbackFilename: string
): Promise<PdfResponse> => {
  try {
    const response = await api.get(path, {
      params: { disposition },
      responseType: 'blob',
      timeout: 30000,
    });

    const contentType = response.headers['content-type'] as string | undefined;
    if (contentType?.includes('application/json')) {
      const text = await (response.data as Blob).text();
      const parsed = JSON.parse(text) as { message?: string };
      throw new Error(parsed.message || 'Failed to generate PDF');
    }

    const filename = parseFilenameFromContentDisposition(
      response.headers['content-disposition'],
      fallbackFilename
    );

    return {
      blob: response.data as Blob,
      filename,
    };
  } catch (error) {
    const message = await parseBlobError(error);
    throw new Error(message);
  }
};
